import { CommentSentiment } from '@core/models/ApiFields/ig-field-values.types';
import { AnyAdvancedMessage } from '@features/inbox/inb-convos/messages/types/msg-advanced-msgs.types';
import {
    AnyAttachment,
    WA_MEDIA_STATUS,
} from '@features/inbox/inb-convos/messages/types/msg-attachment.types';
import { MsgAction, MsgReaction } from '@features/inbox/inb-convos/messages/types/reactions.types';
import { SessionDTO } from '@features/inbox/inb-convos/types/sessions.types';
import {
    WaPtFull,
    WaPtFullDTO,
} from '@shared/features/paid-templates-selector/paid-template-full.types';
import { TemplateActions } from '@shared/features/templates/templates.types';
import { MailboxDTO } from '@features/inbox/inb-convos/types/sessions.types';

export interface EmailParticipant {
    name: string | null;
    email: string;
}

export interface EmailTemplate {
    from: EmailParticipant;
    to: EmailParticipant[];
    cc?: EmailParticipant[];
    bcc?: EmailParticipant[];
    subject?: string;
    text: string;
    html: string;
    attachments: AnyAttachment[];
}

export class MessageModel {
    /** react | unreact */
    action?: MsgAction;
    advanced_message?: AnyAdvancedMessage;
    attachments?: AnyAttachment[];
    auto?: boolean;
    conversation_id: number;
    conversation_session_id: string;
    conversation_session_read_modified?: boolean;
    deleted: boolean;
    echo: boolean;
    edited?: boolean;
    emoji?: string;
    from_me: boolean;
    id: number;
    external_mid: string;
    notificationType: null;
    /** Predetermined reactions (love, wow, ..., other). Only used in some platforms */
    reaction?: MsgReaction;
    read: boolean;
    reply_to?: MessageModel | null;

    // Email specifics
    subject?: string;
    email_html?: string;
    cc?: string[];
    bcc?: string[];
    from_email?: string;
    to_email?: string[];
    email_template?: EmailTemplate;
    mailbox?: MailboxDTO;

    sent_server: boolean;
    sentiment?: CommentSentiment;
    showAvatar?: boolean;
    text?: string | null;
    instruction?: string | null;
    timestamp: string;
    tmp_id?: string;
    twinny_id?: string;
    unsupported?: boolean;
    user_id?: number;
    ai_agent_id?: string;
    whatsappcloud_template?: WaPtFull;
    /** Message error with retry disabled */
    error?: boolean;
    error_desc?: string;
    /** Set on the synthetic first message of an AD-sourced session (see `REFERRAL_TYPE`). */
    referral_type?: ReferralType;
    /** IG/FB ad URL the contact tapped to open the WhatsApp conversation. */
    referral_url?: string;

    /* Email-specific fields */
    // (removed duplicates)

    constructor({
        action,
        advanced_message,
        attachments,
        auto,
        conversation_id,
        conversation_session_id,
        conversation_session_read_modified,
        deleted,
        echo,
        edited,
        emoji,
        error,
        error_desc,
        external_mid,
        from_me,
        id,
        reaction,
        read,
        reply_to,
        sent_server,
        sentiment,
        showAvatar,
        text,
        timestamp,
        tmp_id,
        twinny_id,
        unsupported,
        user_id,
        whatsappcloud_template,
        ai_agent_id,
        email_template,
        mailbox,
        referral_type,
        referral_url,
    }: { id?: number; tmp_id?: string } & MessageDTO & Partial<MessageUiProps>) {
        this.action = action;
        this.advanced_message = advanced_message;
        this.auto = auto;
        this.conversation_id = conversation_id;
        this.conversation_session_id = conversation_session_id;
        this.conversation_session_read_modified = conversation_session_read_modified;
        this.deleted = deleted;
        this.error = error;
        this.echo = echo;
        this.edited = edited;
        this.emoji = emoji;
        this.external_mid = external_mid;
        this.from_me = from_me;
        this.id = id;
        this.notificationType = null;
        this.reaction = reaction;
        this.read = read;
        this.reply_to = reply_to;
        this.sent_server = sent_server;
        this.sentiment = sentiment;
        this.showAvatar = showAvatar;
        this.text = text;
        this.timestamp = timestamp;
        this.tmp_id = tmp_id;
        this.twinny_id = twinny_id;
        this.unsupported = unsupported;
        this.user_id = user_id;
        this.ai_agent_id = ai_agent_id;
        this.error_desc = error_desc;
        this.whatsappcloud_template = whatsappcloud_template
            ? new WaPtFull(whatsappcloud_template)
            : undefined;
        this.email_template = email_template;
        this.mailbox = mailbox;
        this.referral_type = referral_type;
        this.referral_url = referral_url;

        // only whatsapp attachments include mimetype and need to be downloaded
        this.attachments = attachments?.map(att => {
            if (att && att.mimetype && !att.file64) {
                att.downloadStatus = WA_MEDIA_STATUS.pending;
            }
            return att;
        });
    }
}

/* API */

type MessageBase = {
    conversation_id: number;
    conversation_session_id: string;
    deleted: boolean;
    echo: boolean;
    external_mid: string;
    from_me: boolean;
    id: number;
    read: boolean;
    sent_server: boolean;
    timestamp: string;

    action?: MsgAction;
    auto?: boolean;
    conversation_session_read_modified?: boolean;
    edited?: boolean;
    emoji?: string; // for reactions
    reaction?: MsgReaction;
    reply_to?: MessageModel | null;
    sentiment?: CommentSentiment;
    tmp_id?: string;
    twinny_id?: string; // id of contact that sent the message (if it's `from_me` it'll be null)
    unsupported?: boolean;
    user_id?: number;
    ai_agent_id?: string;
    email_template?: EmailTemplate;
    error?: boolean;
    error_desc?: string;
    mailbox?: MailboxDTO;
    referral_type?: ReferralType;
    referral_url?: string;
};

/**
 * Meta click-to-WhatsApp referral. The backend injects a `from_me` message as the first one
 * of an `AD` session whose `id` is the ad click id (`ctwa_clid`).
 */
export const REFERRAL_TYPE = { ad: 'ad' } as const;
export type ReferralType = (typeof REFERRAL_TYPE)[keyof typeof REFERRAL_TYPE];
// Deliberately not a type predicate: narrowing to `never` in the else-branch broke templates.
export const isAdReferralMsg = (msg: unknown): boolean =>
    !!msg && (msg as MessageModel).referral_type === REFERRAL_TYPE.ad;

export type MessageDTO = MessageBase & MessageContent;
export type MessageWithConvoSessionDTO = MessageDTO & { conversation_session: SessionDTO };

export type MessageText = { text: string | null; instruction?: string | null };
export type MessageEmail = {
    subject?: string;
    email_subject?: string;
    email_participants?: {
        to?: string[];
        cc?: string[];
        bcc?: string[];
    };
    email_html?: string;
    to_email?: string[];
    from_email?: string;
    from_name?: string;
    to?: string[];
    cc?: string[];
    bcc?: string[];
};

export type MessageAttachment = { attachments: AnyAttachment[] };
export type MessageAdvancedMsg = { advanced_message: AnyAdvancedMessage };
export type MessagePaidTemplate = { whatsappcloud_template: WaPtFullDTO };

export type MessageContent = Partial<MessageText> &
    Partial<MessageEmail> &
    Partial<MessageAttachment> &
    Partial<MessageAdvancedMsg> &
    Partial<MessagePaidTemplate>;

export type NewMessagePload = (MessageContent | ShareProductPload) & { tmp_id: string };
export type ShareProductPload = {
    product_ids: number[];
    action: TemplateActions;
};

/* UI ONLY */

export type TempNewMessage = MessageContent & {
    id: number;
    from_me: boolean;
    action: MsgAction | null;
    reaction: MsgReaction | null;
};

export type MessageUiProps = {
    showAvatar?: boolean;
};
